import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../baseQuery';

type CreatePortalSessionRequest = Readonly<{
  returnUrl: string;
}>;

interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  subscriptionId?: string;
  status?: 'active' | 'canceled' | 'past_due' | 'trialing';
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  planName?: string;
  priceId?: string;
}

interface PortalSessionResponse {
  url: string;
}

type CreateCheckoutSessionRequest = Readonly<{
  priceId: string;
}>;

type CreateCheckoutSessionResponse = Readonly<{
  sessionId: string;
  url: string;
  clientSecret: string;
}>;

type CheckoutSessionDetails = Readonly<{
  sessionId: string;
  paymentStatus: string;
  customerEmail?: string;
  customerName?: string;
  subscriptionId?: string;
  amountTotal?: number;
  currency?: string;
  lineItems?: {
    description: string;
    amount: number;
    currency: string;
  }[];
}>;

type PaymentSessionProvisionResponse = Readonly<{
  provision_status: 'success' | 'failed';
  session_status: string;
  familyAccount?: {
    id: number | null; // null if webhook hasn't created the family account yet
    seats: number;
  } | null;
}>;

type PromoData = Readonly<{
  type: 'free_trial' | 'intro_price';
  displayText: string | null;
  periodText: string | null;
}>;

export type SubscriptionPlan = Readonly<{
  id: string;
  productId: string;
  name: string;
  description: string;
  amount: number;
  currency: string;
  interval: 'month' | 'year';
  intervalCount: number;
  features: string[];
  recommended: boolean;
  active: boolean;
  displayOrder: number;
  images: string[];
  iconUrl?: string;
  marketingFeatures?: {
    name: string;
  }[];
  promoData?: PromoData | null;
}>;

type SubscriptionPlansResponse = Readonly<{
  plans: SubscriptionPlan[];
}>;

export const subscriptionApi = createApi({
  reducerPath: 'subscriptionApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Subscription'],
  endpoints: (builder) => ({
    // Get available subscription plans from Stripe
    getSubscriptionPlans: builder.query<SubscriptionPlansResponse, undefined>({
      query: () => '/subscriptions/plans/grow',
    }),

    // Get current subscription status
    getCurrentSubscription: builder.query<SubscriptionStatus, undefined>({
      query: () => '/subscriptions/status',
      providesTags: ['Subscription'],
    }),

    // Create Customer Portal session for billing management
    createPortalSession: builder.mutation<PortalSessionResponse, CreatePortalSessionRequest>({
      query: (body) => ({
        url: '/subscriptions/create-portal-session',
        method: 'POST',
        body,
      }),
    }),

    // Create Checkout Session for subscription
    createCheckoutSession: builder.mutation<
      CreateCheckoutSessionResponse,
      CreateCheckoutSessionRequest
    >({
      query: (body) => ({
        url: '/subscriptions/create-checkout-session',
        method: 'POST',
        body,
      }),
    }),

    // Get checkout session details by session ID
    getCheckoutSession: builder.query<CheckoutSessionDetails, string>({
      query: (sessionId) => `/subscriptions/checkout-session/${sessionId}`,
    }),

    // Provision payment session (handles provisional entitlements)
    provisionPaymentSession: builder.mutation<
      PaymentSessionProvisionResponse,
      { sessionId: string }
    >({
      query: ({ sessionId }) => ({
        url: `/subscriptions/payment-session/provision?session_id=${sessionId}`,
        method: 'GET',
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Import userApi dynamically to avoid circular dependency
          const { userApi } = await import('../user/user.api');

          dispatch(userApi.util.invalidateTags(['UserPermissions']));
        } catch (error) {
          console.error('Provision failed:', error);
        }
      },
    }),

    // STRETCH GOAL: Cancel subscription (at period end)
    // Preferred approach is Customer Portal for subscription management
    /*
    cancelSubscription: builder.mutation<void, void>({
      query: () => ({
        url: '/subscriptions/cancel',
        method: 'POST',
      }),
      invalidatesTags: ['Subscription'],
    }),
    */
  }),
});

export const {
  useGetSubscriptionPlansQuery,
  useGetCurrentSubscriptionQuery,
  useCreatePortalSessionMutation,
  useCreateCheckoutSessionMutation,
  useGetCheckoutSessionQuery,
  useProvisionPaymentSessionMutation,
  // useCancelSubscriptionMutation, // Commented out - stretch goal
} = subscriptionApi;
